import {z} from 'zod';
import React, {FC} from 'react';
import {toast} from 'react-toastify';
import {observer} from 'mobx-react-lite';
import {useNavigate} from 'react-router-dom';
import {zodResolver} from '@hookform/resolvers/zod';
import {FormProvider, useForm} from 'react-hook-form';
import {ArrowTopRightOnSquareIcon, BuildingOffice2Icon} from '@heroicons/react/24/outline';
import {getOrganization, updateOrganization, VerdocsEndpoint} from '@verdocs/js-sdk';
import {convertToE164, IOrganization, updateOrganizationLogo, updateOrganizationThumbnail} from '@verdocs/js-sdk';
import {Button, ColorPicker, FlexFill, FormHint, FormLabel, HelpDialog, PageTitle, PhotoSelector, TextInput} from '../../Components';
import {invalidateAllQueries} from '../../react-sdk/queries';
import {track} from '../../Managers/Analytics';
import {AppState} from '../../AppState';
import {URLS} from '../../Routing';

const schema = z.object({
  name: z.string().trim().min(1, 'Name is required').max(30),
  address: z.string(),
  address2: z.string(),
  phone: z.union([z.literal(''), z.string().trim()]).pipe(z.transform((val) => convertToE164(val))),
  contact_email: z.union([z.literal(''), z.email()]),
  url: z.union([z.literal(''), z.string()]),
  primary_color: z.string().trim().optional(),
  secondary_color: z.string().trim().optional(),
  full_logo_url: z.string().trim().optional(),
  thumbnail_url: z.string().trim().optional(),
  terms_use_url: z.string().nullish(),
  privacy_policy_url: z.string().nullish(),
  powered_by_url: z.string().nullish(),
  powered_by_label: z.string().nullish(),
  // No longer editable here, but it round-trips so a value saved earlier is not wiped by an unrelated save.
  style_overrides: z.string().optional().or(z.literal('')),
});

type SchemaType = z.infer<typeof schema>;

const getEditableFields = (organization?: IOrganization) => ({
  name: organization?.name || '',
  address: organization?.address || '',
  address2: organization?.address2 || '',
  phone: organization?.phone || '',
  contact_email: organization?.contact_email || '',
  url: organization?.url || '',
  primary_color: organization?.primary_color || '',
  secondary_color: organization?.secondary_color || '',
  full_logo_url: organization?.full_logo_url || '',
  thumbnail_url: organization?.thumbnail_url || '',
  terms_use_url: organization?.terms_use_url || '',
  privacy_policy_url: organization?.privacy_policy_url || '',
  powered_by_url: organization?.powered_by_url || '',
  powered_by_label: organization?.powered_by_label || '',
  style_overrides: (organization as any)?.style_overrides || '',
});

const Card: FC<{children: React.ReactNode; className?: string}> = ({children, className = ''}) => (
  <div className={`rounded-lg border border-[#ebecf0] bg-white p-5 ${className}`}>{children}</div>
);

const SectionTitle: FC<{children: React.ReactNode}> = ({children}) => (
  <div className="mb-3 text-[15px] font-medium text-[#33364b]">{children}</div>
);

const TwoUp: FC<{children: React.ReactNode}> = ({children}) => <div className="grid grid-cols-1 gap-x-4 tablet:grid-cols-2">{children}</div>;

export const Organization: FC = observer(() => {
  const navigate = useNavigate();
  const organizationId = AppState.currentProfile?.organization_id || '';

  const [newLogo, setNewLogo] = React.useState<File | null>(null);
  const [newThumbnail, setNewThumbnail] = React.useState<File | null>(null);

  const form = useForm<SchemaType>({
    mode: 'all',
    resolver: zodResolver(schema),
    defaultValues: async () => getOrganization(VerdocsEndpoint.getDefault(), organizationId).then(getEditableFields),
  });

  const onSubmit = async (data: any) => {
    if (newLogo) {
      await updateOrganizationLogo(VerdocsEndpoint.getDefault(), organizationId, newLogo)
        .then(() => setNewLogo(null))
        .catch((e) => console.log('Unknown error', e));
    }

    if (newThumbnail) {
      await updateOrganizationThumbnail(VerdocsEndpoint.getDefault(), organizationId, newThumbnail)
        .then(() => setNewThumbnail(null))
        .catch((e) => console.log('Unknown error', e));
    }

    await updateOrganization(VerdocsEndpoint.getDefault(), organizationId, {
      name: data.name,
      address: data.address,
      address2: data.address2,
      phone: data.phone,
      contact_email: data.contact_email,
      url: data.url,
      primary_color: data.primary_color,
      secondary_color: data.secondary_color,
      terms_use_url: data.terms_use_url.trim() || null,
      privacy_policy_url: data.privacy_policy_url.trim() || null,
      powered_by_url: data.powered_by_url.trim() || null,
      powered_by_label: data.powered_by_label.trim() || null,
      style_overrides: data.style_overrides?.trim() || null,

      // Left here for documentation. We edit these through a different path.
      // full_logo_url: data.full_logo_url,
      // thumbnail_url: data.thumbnail_url,
    })
      .then((r) => {
        form.reset(getEditableFields(r));
        invalidateAllQueries();
        track('settings_updated', {section: 'organization'});
        toast.success('Organization updated');
      })
      .catch((e) => toast.error(`Error updating organization: ${e.message}`));
  };

  const dirty = form.formState.isDirty || newLogo || newThumbnail;
  const name = form.watch('name');
  const url = form.watch('url');
  const logoUrl = form.watch('full_logo_url');
  const thumbnailUrl = form.watch('thumbnail_url');

  return (
    <div className="w-full max-w-[860px] pb-10">
      <PageTitle>
        Organization Profile
        <FlexFill />
        <HelpDialog title="Organization profile">
          Your organization's contact details, plus the legacy logo, color, attribution, and footer link settings that Branding is
          replacing. Organizations that have set up a brand already use it instead of the settings below the notice.
        </HelpDialog>
      </PageTitle>

      <FormProvider {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)}>
          <Card className="mb-4">
            <div className="flex flex-wrap items-center gap-5">
              <div className="flex h-14 w-14 flex-shrink-0 items-center justify-center overflow-hidden rounded-lg border border-[#ebecf0] bg-[#fbfbfc]">
                {thumbnailUrl ? (
                  <img src={thumbnailUrl} alt="" className="max-h-12 max-w-12 object-contain" />
                ) : (
                  <BuildingOffice2Icon className="h-7 w-7 text-gray-300" />
                )}
              </div>
              <div className="min-w-0 flex-1">
                <div className="truncate text-xl font-medium text-[#33364b]">{name || 'Unnamed organization'}</div>
                <div className="mt-0.5 truncate text-[13px] text-gray-500">{url || 'No web site set'}</div>
              </div>
            </div>
          </Card>

          <Card className="mb-4">
            <SectionTitle>Company details</SectionTitle>
            <TextInput label="Organization name" name="name" required />
            <TwoUp>
              <TextInput label="Web site" name="url" placeholder="https://example.com" />
              <TextInput label="Contact phone" name="phone" placeholder="+1 555 555 5555" />
            </TwoUp>
            <TextInput label="Contact email" name="contact_email" placeholder="support@example.com" />
            <TwoUp>
              <TextInput label="Address" name="address" />
              <TextInput label="Address line 2" name="address2" />
            </TwoUp>
          </Card>

          <div className="mb-4 flex flex-wrap items-center gap-3 rounded-lg border border-[#f0d9b5] bg-[#fdf8ef] p-4">
            <div className="min-w-[240px] flex-1 text-[13px] text-[#95591a]">
              <span className="font-medium">NOTE:</span> These settings are being replaced by our new Branding feature.
            </div>
            <Button
              size="small"
              variant="secondary"
              label="Try Branding Now"
              icon={<ArrowTopRightOnSquareIcon className="h-4 w-4" />}
              onClick={() => navigate(URLS.SETTINGS_BRANDING.build())}
            />
          </div>

          <Card className="mb-4">
            <SectionTitle>Logo and thumbnail</SectionTitle>
            <TwoUp>
              <div className="mb-4">
                <FormLabel>Logo</FormLabel>
                <PhotoSelector newFile={newLogo} value={logoUrl} onSelectFile={setNewLogo} />
                <FormHint>Wide-form logo, used at the top of email templates.</FormHint>
              </div>
              <div className="mb-4">
                <FormLabel>Thumbnail</FormLabel>
                <PhotoSelector newFile={newThumbnail} value={thumbnailUrl} onSelectFile={setNewThumbnail} />
                <FormHint>Square version for small spaces.</FormHint>
              </div>
            </TwoUp>
          </Card>

          <Card className="mb-4">
            <SectionTitle>Colors</SectionTitle>
            <TwoUp>
              <div>
                <FormLabel>Primary color</FormLabel>
                <ColorPicker name="primary_color" defaultValue="#654dcb" />
                <FormHint className="-mt-3 mb-4">Background in email templates. Text on it is white, so keep it dark.</FormHint>
              </div>
              <div>
                <FormLabel>Secondary color</FormLabel>
                <ColorPicker name="secondary_color" defaultValue="#55bc81" />
                <FormHint className="-mt-3 mb-4">Buttons and accents.</FormHint>
              </div>
            </TwoUp>
          </Card>

          <Card className="mb-4">
            <SectionTitle>Powered by</SectionTitle>
            <div className="mb-4 text-[13px] text-gray-500">
              A text label in the lower-left corner of the signing experience. Leave the label empty to hide it; add a URL to make it a
              link.
            </div>
            <TwoUp>
              <TextInput label="Label" name="powered_by_label" placeholder="Powered by Verdocs" />
              <TextInput label="URL" name="powered_by_url" placeholder="https://verdocs.com" />
            </TwoUp>
          </Card>

          <Card className="mb-4">
            <SectionTitle>Signing footer links</SectionTitle>
            <div className="mb-4 text-[13px] text-gray-500">Optional links shown in the signing footer. Leave either empty to hide it.</div>
            <TwoUp>
              <TextInput label="Terms of use" name="terms_use_url" placeholder="https://example.com/terms" />
              <TextInput label="Privacy policy" name="privacy_policy_url" placeholder="https://example.com/privacy" />
            </TwoUp>
          </Card>

          <Button type="submit" label="Save changes" disabled={form.formState.isSubmitting || !dirty} />
        </form>
      </FormProvider>
    </div>
  );
});
